// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import glamorous, { Div } from 'glamorous';
import { borderWidth, tint } from 'polished';
import { memoize } from 'lodash/fp';

import oneScoreImage from 'app/images/onesport/onescore.png';
import Loader from '../../components/commons/Loader';
import RenderFirst from '../../components/util/RenderFirst';
import Form from './components/Form';
import GameLogRapidFire from './components/GameLogRapidFire';
import Glance from './components/Glance';
import SeasonLog from './components/SeasonLog';
import News from './components/News';
import RacingOverview from './components/RacingOverview';

type Props = {
  displayLogo: boolean,
  isGolf: boolean,
  isLoading: boolean,
  isRacing?: boolean,
  player?: Object,
  error?: string
};

type State = {
  activeTab: 'glance' | 'seasons' | 'form' | 'game-log' | 'news'
};

const Wrapper = glamorous.div(
  ({ theme }) => ({
    backgroundColor: tint(0.5, theme.color.basis6),
    color: theme.color.basis2,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Roboto',
    flexGrow: 1,
    minWidth: 0,
    padding: '10px 0px',
    overflow: 'scroll'
  }),
  ({ theme }) => ({
    [theme.mediaQuery.tabletLandscapeAndUp]: {
      maxWidth: 'auto',
      width: '47vw'
    },
    [theme.mediaQuery.desktopAndUp]: {
      maxWidth: '362px'
    }
  })
);

const TabList = glamorous.div(({ theme }) => ({
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  listStyle: 'none',
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    marginLeft: 'auto'
  }
}));

const Tab = glamorous('button', {
  withProps: {
    type: 'button'
  }
})(
  ({ theme }) => ({
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    fontSize: theme.fontSize.tiny,
    outline: 0,
    padding: '0.75em 2em',
    [theme.mediaQuery.tabletLandscapeAndUp]: {
      flexBasis: 'auto',
      flexGrow: 0,
      flexShrink: 0
    }
  }),
  ({ active, theme }) =>
    active
      ? {
          backgroundColor: theme.color.white,
          color: theme.color.black
        }
      : {
          backgroundColor: theme.color.basis6,
          color: theme.color.basis4
        },
  ({ disabled }) =>
    disabled
      ? {
          cursor: 'not-allowed !important',
          opacity: 0.5
        }
      : undefined
);

const Content = glamorous.div(({ theme }) => ({
  alignItems: 'flex-start',
  backgroundColor: theme.color.white,
  borderColor: theme.color.basis5,
  borderStyle: 'solid',
  ...borderWidth(0, 1, 1),
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    maxHeight: 540
  }
}));

const Foot = glamorous.div({
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  marginTop: 'auto',
  paddingTop: 5
});

const FootImage = glamorous('img', {
  withProps: {
    src: oneScoreImage,
    alt: 'OneScore'
  }
})({
  height: 16,
  margin: 'auto',
  width: 'auto'
});

class OneScoreWidgetCoreRapidFire extends React.Component<Props, State> {
  props: Props;

  state: State = {
    activeTab: 'form'
  };

  static defaultProps = {
    isRacing: false,
    player: undefined,
    error: undefined
  };

  handleTabChange = memoize((tab: 'glance' | 'seasons' | 'form' | 'game-log' | 'news') => () => {
    this.setState({
      activeTab: tab
    });
  });

  render() {
    const { displayLogo, isGolf, isLoading, isRacing, player, error } = this.props;
    const { activeTab } = this.state;

    const gameLogTab = isGolf ? 'seasons' : 'game-log';

    return (
      <Wrapper>
        <TabList>
          {isGolf && (
            <Tab active={activeTab === 'glance'} onClick={this.handleTabChange('glance')}>
              At A Glance
            </Tab>
          )}
          <Tab active={activeTab === 'form'} onClick={this.handleTabChange('form')}>
            {isRacing ? 'Overview' : 'Form'}
          </Tab>
          <Tab active={activeTab === gameLogTab} onClick={this.handleTabChange(gameLogTab)}>
            {isRacing ? 'Recent' : 'Game Log'}
          </Tab>
          <Tab active={activeTab === 'news'} onClick={this.handleTabChange('news')}>
            News
          </Tab>
        </TabList>
        <Content>
          <RenderFirst>
            {isLoading && !player && (
              <Div margin="0 auto" padding={10}>
                <Loader kind="dark" />
              </Div>
            )}
            {error && (
              <Div margin="0 auto" padding={10}>
                {error}
              </Div>
            )}
            {player && (
              <React.Fragment>
                {activeTab === 'glance' && <Glance season={player} />}
                {activeTab === 'seasons' && <SeasonLog season={player} />}
                {activeTab === 'form' &&
                  (isRacing ? (
                    <RacingOverview data={player} />
                  ) : (
                    <Form player={player} isSmall={true} />
                  ))}
                {activeTab === 'game-log' && (
                  <GameLogRapidFire
                    player={player}
                    isRacing={isRacing}
                    fixedColumnCount={isRacing ? 1 : 3}
                  />
                )}
                {activeTab === 'news' && <News player={player} />}
              </React.Fragment>
            )}
          </RenderFirst>
        </Content>
        {displayLogo && (
          <Foot>
            <FootImage />
          </Foot>
        )}
      </Wrapper>
    );
  }
}

function mapStateToProps(state) {
  return {
    displayLogo: state.settings.onesport
  };
}

export default connect(mapStateToProps)(OneScoreWidgetCoreRapidFire);
